import CT from '@marketplace-ai/tags';
import { Fragment } from 'react';
import { Link, useLocation } from 'react-router';
import { Button } from '~/components/buttons/Button/Button';
import { useBrandConfig } from '~/config/branding';
import { useMediaQuery } from '~/hooks/useMediaQuery';
import { buildBrandAuthEntryUrl } from '~/routing/brandSwitch';
import {
  CLERK_REDIRECT_URL_PARAM,
  readAuthHandoffUrl,
  RETURN_TO_PARAM,
  ROLE_INTENT_PARAM,
  sanitizeAuthHandoffUrl,
  sanitizeReturnTo,
} from '~/routing/returnTo';
import { paths } from '~/routing/routes';
import { LANDING_CONTENT } from '../landing-page/landingContent';
import { LoginImages } from './LoginImages';

/** Appends a validated Clerk hand-off target to an in-app or absolute href. */
const withHandoff = (href: string, handoff: string | null): string => {
  if (!handoff) return href;
  const url = new URL(href, window.location.origin);
  url.searchParams.set(CLERK_REDIRECT_URL_PARAM, handoff);
  return href.startsWith('http') ? url.toString() : `${url.pathname}${url.search}`;
};

export const LoginOrCreateAccountPage = () => {
  // [MCV-1666] The CHOICE, not the current brand, decides which host's Clerk
  // sign-in card the visitor lands on: a provider always ends up on
  // MedCredVault, an organization always ends up on HealthcareLX. Both
  // choices land on the SAME combined sign-in/sign-up card
  // (`SignInPage`/`<SignIn withSignUp>`), carrying a `role` query param that
  // `SignInPage` turns into a `setPendingRole` call before any satellite
  // redirect can run -- see `pendingRoleIntent.ts`.
  //
  // MedCredVault is provider-only, so its organization CTA hands off to
  // HealthcareLX, and HealthcareLX's provider CTA hands off to MedCredVault.
  // Cross-host links use `buildBrandAuthEntryUrl`, NOT `buildBrandSwitchUrl`:
  // the latter stamps `__clerk_synced`, which the receiving host reads as
  // "Clerk is handing the browser back", suppressing the hand-off.
  //
  // This page mounts no Clerk component, so a `redirect_url` preserved from
  // an HLX-originated sign-in would die here: without forwarding it, a
  // visitor who came from HLX and switched hosts would finish on the wrong
  // brand instead of being returned to where they started.
  const { brand } = useBrandConfig();
  const location = useLocation();
  // The hero is decorative and only shown from md up. Not rendering it below
  // md (instead of `hidden`) keeps phones from downloading the eager image.
  const isMd = useMediaQuery('(min-width: 768px)');
  const content = LANDING_CONTENT[brand];

  const handoff = sanitizeAuthHandoffUrl(
    readAuthHandoffUrl(location.search, location.hash),
    window.location.origin
  );

  const returnTo = sanitizeReturnTo(
    new URLSearchParams(location.search).get(RETURN_TO_PARAM),
    window.location.origin
  );

  const buildChoiceHref = (targetBrand: 'mcv' | 'hlx', role: 'provider' | 'employer'): string => {
    const params = new URLSearchParams({ [ROLE_INTENT_PARAM]: role });
    if (returnTo) params.set(RETURN_TO_PARAM, returnTo);
    const signInPath = `${paths.publicAuth.signIn}?${params.toString()}`;

    return withHandoff(
      targetBrand === brand ? signInPath : buildBrandAuthEntryUrl(targetBrand, signInPath),
      handoff
    );
  };

  const providerHref = buildChoiceHref('mcv', 'provider');
  const employerHref = buildChoiceHref('hlx', 'employer');

  return (
    // 5rem mirrors the public shells' pt-20 header offset (PublicApp.tsx, app.tsx); change both together.
    <div className="flex flex-col items-center justify-center min-h-[calc(100svh-5rem)] md:min-h-screen p-4 bg-primary-t-950">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 w-full max-w-6xl">
        {isMd && (
          <div className="flex justify-center">
            <LoginImages />
          </div>
        )}
        <div className="text-left flex flex-col items-start min-w-0">
          {/* The trailing `!` is needed: .heading-large-sb / .sub-heading in styles.css are unlayered and beat Tailwind utilities. */}
          <h1 className="heading-large-sb max-md:text-[30px]! max-md:leading-9! mb-6 md:mb-16">
            {content.headline.map((line, index) => (
              <Fragment key={index}>
                {index > 0 && <br />}
                {line}
              </Fragment>
            ))}
          </h1>
          <h2 className="sub-heading max-md:text-[16px]! max-md:leading-6! text-grey-300 mb-3">
            <i>{content.subheadline}</i>
          </h2>
          <p className="sub-heading max-md:text-[16px]! max-md:leading-6! text-grey-400 mb-6 md:mb-12">
            {content.description}
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 justify-start w-full">
            {/* Same-host on MCV, cross-host to MCV on HLX -- so this is a plain
                <a> on HLX, not a react-router <Link>, which would treat the
                absolute URL as an in-app path and 404. */}
            {brand === 'mcv' ? (
              <Link to={providerHref} className="w-full sm:w-auto">
                <Button
                  cyTag={CT.authentication.signUpButton}
                  variant="rounded"
                  className="w-full sm:w-auto"
                >
                  I'm a provider
                </Button>
              </Link>
            ) : (
              <a href={providerHref} className="w-full sm:w-auto">
                <Button
                  cyTag={CT.authentication.signUpButton}
                  variant="rounded"
                  className="w-full sm:w-auto"
                >
                  I'm a provider
                </Button>
              </a>
            )}
            {/* Same-host on HLX, cross-host to HLX on MCV. */}
            {brand === 'hlx' ? (
              <Link to={employerHref} className="w-full sm:w-auto">
                <Button
                  cyTag={CT.authentication.signUpEmployerButton}
                  variant="rounded"
                  className="w-full sm:w-auto"
                >
                  I'm an organization
                </Button>
              </Link>
            ) : (
              <a href={employerHref} className="w-full sm:w-auto">
                <Button
                  cyTag={CT.authentication.signUpEmployerButton}
                  variant="rounded"
                  className="w-full sm:w-auto"
                >
                  I'm an organization
                </Button>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
